// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Favourites  (route /favourites — the catches a signed-in customer saved with the heart)
// Saved catches (newest first) with the real photo, price for the chosen pack, the saved cut (changeable) and a pack toggle
// (500 g / 1 kg); "Add to cart" puts one pack of that size, in that cut, in the cart. Changing the cut or pack updates the saved
// preference. Category chips filter the list with counts. Stored server-side (hooks/useFavouriteCatches → /api/me/favourites).
// Deliberately NOT here (no data/system behind it): cold-chain / grade / landing badges, "packs left today", net weight after
// peeling, "Restocking" + "Notify me on arrival" (no stock or landing schedule per catch), the "Cuts" filter button.
// Neighbours: components/menu/FavouriteHeart (where catches get saved), data/cuts + data/packs (options and prices), hooks/useCart.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Check, CircleAlert, Heart, Loader2, Scissors, ShoppingCart, Sparkles } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { cutOf, cutsFor, prepFee, type CutId } from "../data/cuts";
import { PACK_SIZES, packPrice, type PackId } from "../data/packs";
import { SEAFOOD_CATEGORIES, seafoodItems, type SeafoodCategory, type SeafoodItem } from "../data/seafood";
import useAuth from "../hooks/useAuth";
import useCart from "../hooks/useCart";
import { MAX_PACKS } from "../hooks/useCatchDraft";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useFavouriteCatches from "../hooks/useFavouriteCatches";
import type { FavouriteCatch } from "../utils/favouriteCatches";
import { formatInr } from "../utils/priceFormat";
import { getWikimediaSrcSet } from "../utils/wikimediaImage";

type Filter = "all" | SeafoodCategory;

interface CardProps {
  item: SeafoodItem;
  saved: FavouriteCatch;
  onRemove: () => void;
  onChange: (choice: { cut: CutId; pack: PackId }) => void;
}

function SavedCard({ item, saved, onRemove, onChange }: CardProps) {
  const { qtyOf, setQty } = useCart();
  const cut = cutOf(item, saved.cut ?? undefined);
  const pack = PACK_SIZES.find((size) => size.id === saved.pack) ?? PACK_SIZES[0];
  const [added, setAdded] = useState(false);
  const inCart = qtyOf(item.id, pack.id);
  const full = inCart >= MAX_PACKS;
  const price = packPrice(item, pack) + prepFee(cut, pack);

  const add = () => {
    setQty(item.id, pack.id, Math.min(MAX_PACKS, inCart + 1), cut.id);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1600);
  };

  return (
    <li className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10" data-testid="favourite-card">
      <div className="flex gap-3">
        <Link to={`/menu/${item.id}`} tabIndex={-1} aria-hidden="true" className="shrink-0">
          <img
            src={item.image}
            srcSet={getWikimediaSrcSet(item.image)}
            sizes="112px"
            alt=""
            title={item.credit}
            loading="lazy"
            width={112}
            height={112}
            className="h-28 w-28 rounded-xl object-cover"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {item.tags.length > 0 && <p className="mb-1 truncate text-[11px] font-bold text-teal-700 dark:text-teal-300">{item.tags.join(" • ")}</p>}
              <h2 className="font-display text-lg font-bold leading-snug text-ocean-950 dark:text-foam-50">
                <Link to={`/menu/${item.id}`} className="hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">
                  {item.name}
                </Link>
              </h2>
              <p className="text-xs italic text-ocean-800/75 dark:text-foam-100/70">{item.scientificName}</p>
            </div>
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${item.name} from favourites`}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-coral-500/10 text-coral-600 hover:bg-coral-500/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:text-coral-300"
            >
              <Heart className="h-5 w-5 fill-current" aria-hidden="true" />
            </button>
          </div>
          <p className="mt-auto pt-1">
            <span className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">{formatInr(price)}</span>
            <span className="ml-1 text-xs text-ocean-800/70 dark:text-foam-100/65">/ {pack.label}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-xl bg-ocean-700/5 p-2.5 dark:bg-white/5">
        <Scissors className="h-5 w-5 shrink-0 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <label htmlFor={`cut-${item.id}`} className="block text-[11px] font-bold uppercase tracking-wide text-ocean-800 dark:text-foam-100">
            {saved.cut ? "Saved cut" : "Cut"}
          </label>
          <select
            id={`cut-${item.id}`}
            value={cut.id}
            onChange={(event) => onChange({ cut: event.target.value as CutId, pack: pack.id })}
            className="mt-0.5 w-full truncate rounded-md bg-transparent text-sm text-ocean-950 focus-visible:outline-2 focus-visible:outline-teal-600 dark:text-foam-50"
          >
            {cutsFor(item).map((option) => (
              <option key={option.id} value={option.id} className="text-ocean-950">
                {option.name}
                {option.prepFeePerKg > 0 ? ` (+₹${option.prepFeePerKg}/kg)` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div role="radiogroup" aria-label={`Pack size for ${item.name}`} className="flex rounded-full bg-ocean-700/10 p-0.5 dark:bg-white/10">
          {PACK_SIZES.map((size) => {
            const on = size.id === pack.id;
            return (
              <button
                key={size.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onChange({ cut: cut.id, pack: size.id })}
                className={`min-h-11 rounded-full px-4 text-sm font-bold focus-visible:outline-2 focus-visible:outline-teal-600 ${on ? "bg-white text-ocean-900 shadow-sm dark:bg-ocean-800 dark:text-foam-50" : "text-ocean-800/80 dark:text-foam-100/75"}`}
              >
                {size.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={add}
          disabled={full}
          aria-label={`Add 1 × ${pack.label} ${item.name}, ${cut.name}, to cart`}
          className={`inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-full px-4 text-sm font-bold text-white shadow-md disabled:opacity-60 ${added ? "bg-teal-700" : "bg-ocean-800 hover:bg-ocean-700"}`}
        >
          {added ? <Check className="h-4 w-4" aria-hidden="true" /> : <ShoppingCart className="h-4 w-4" aria-hidden="true" />}
          {added ? "Added" : full ? "Cart full" : "Add to cart"}
        </button>
      </div>
      <p role="status" className="sr-only">{added ? `Added 1 × ${pack.label} ${item.name} to cart` : ""}</p>
      {inCart > 0 && (
        <p className="text-xs font-semibold text-teal-700 dark:text-teal-300">
          {inCart} × {pack.label} of this catch in your cart
        </p>
      )}
    </li>
  );
}

export default function Favourites() {
  useDocumentTitle("Favourites");
  const auth = useAuth();
  const favourites = useFavouriteCatches();
  const [filter, setFilter] = useState<Filter>("all");

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  // Saved rows that no longer match a catch on the menu (a removed product) are simply not shown.
  const saved = (favourites.rows ?? []).flatMap((row) => {
    const item = seafoodItems.find((candidate) => candidate.id === row.catchId);
    return item ? [{ row, item }] : [];
  });
  const countOf = (category: SeafoodCategory) => saved.filter(({ item }) => item.category === category).length;
  const shown = saved.filter(({ item }) => filter === "all" || item.category === filter);
  const loading = favourites.rows === null && !favourites.failed;

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Favourites</h1>
              <p className="flex items-center gap-1.5 text-xs text-ocean-800/80 dark:text-foam-100/75">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-teal-600" />
                {saved.length} saved {saved.length === 1 ? "catch" : "catches"}
              </p>
            </div>
          </header>

          <Link to="/coming-soon/notify-arrival" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Restock alerts on favourites</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          <p role="alert" className="flex min-h-5 items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">
            {favourites.error && <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
            {favourites.error}
          </p>

          {loading && (
            <p role="status" className="mt-8 flex items-center justify-center gap-2 text-ocean-900 dark:text-foam-50">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading your favourites…
            </p>
          )}

          {favourites.failed && (
            <div role="alert" className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-semibold text-ocean-950 dark:text-foam-50">We couldn&apos;t load your favourites.</p>
              <button type="button" onClick={() => void favourites.reload()} className="mt-3 min-h-11 rounded-full bg-ocean-800 px-6 font-bold text-white">
                Try again
              </button>
            </div>
          )}

          {favourites.rows !== null && saved.length === 0 && (
            <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-ocean-900">
              <Heart className="mx-auto h-10 w-10 text-coral-600" aria-hidden="true" />
              <p className="mt-3 font-display text-lg font-bold text-ocean-950 dark:text-foam-50">No saved catches yet</p>
              <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">Tap the heart on any catch to keep it here, with the cut and pack size you like.</p>
              <Link to="/menu" className="mt-4 inline-flex min-h-11 items-center rounded-full bg-ocean-800 px-6 font-bold text-white hover:bg-ocean-700">
                Browse the menu
              </Link>
            </div>
          )}

          {saved.length > 0 && (
            <>
              <div role="group" aria-label="Filter by category" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
                {(["all", ...SEAFOOD_CATEGORIES] as Filter[]).map((option) => {
                  const on = filter === option;
                  const count = option === "all" ? saved.length : countOf(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setFilter(option)}
                      className={`min-h-11 shrink-0 rounded-full px-4 text-sm font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${on ? "bg-ocean-800 text-white shadow-sm" : "bg-white text-ocean-900 ring-1 ring-ocean-900/10 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10"}`}
                    >
                      {option === "all" ? "All" : option} ({count})
                    </button>
                  );
                })}
              </div>

              {shown.length === 0 && <p className="text-center text-sm text-ocean-800/85 dark:text-foam-100/80">Nothing saved in this category yet.</p>}
              <ul className="flex flex-col gap-4">
                {shown.map(({ row, item }) => (
                  <SavedCard
                    key={item.id}
                    item={item}
                    saved={row}
                    onRemove={() => void favourites.remove(item.id)}
                    onChange={(choice) => void favourites.save(item.id, choice)}
                  />
                ))}
              </ul>

              <div className="flex items-start gap-3 rounded-2xl bg-ocean-700/5 p-4 dark:bg-white/5">
                <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-sm font-bold text-ocean-900 dark:text-foam-50">Quick add</h2>
                  <p className="mt-0.5 text-xs text-ocean-800/85 dark:text-foam-100/80">
                    &ldquo;Add to cart&rdquo; uses the cut and pack size saved here. Change either and it is remembered for next time.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </PageTransition>
  );
}
